import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { dayKey, formatDateTime } from '../ui/format';
import { Badge, EmptyState, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  getAgenda,
  isBusy,
  listBookingLinks,
  listBookings,
  listCalendars,
  listWindow,
  setCalendarPreference,
  updateEvent,
  type AgendaItem,
  type BookingRow,
  type WindowItem,
} from './api';
import { AvailabilityPanel } from './AvailabilityPanel';
import { SyncPanel } from './SyncPanel';
import { EventDialog, type EventDraft } from './EventDialog';
import { TimeGrid } from './TimeGrid';
import { MiniCalendar } from './MiniCalendar';

/**
 * The week, as somebody actually works it.
 *
 * Six ways to look at the same days — one day, a working week, a full week, a month, a year and a
 * plain list — because the question changes with the range. "Where am I at three" is a day; "can I
 * fit this in" is a week; "when is that trip" is a year. A product with only a month grid answers
 * one of those.
 *
 * Every view reads the same window query, so nothing can disagree about what is happening. The grid
 * views carry the gestures (click a slot, drag a range, drag a block, drag its edge); the month and
 * year views are for navigating, and clicking a day in them lands you in that day.
 */

type ViewKind = 'DAY' | 'WORK_WEEK' | 'WEEK' | 'MONTH' | 'YEAR' | 'SCHEDULE';

const GRID_VIEWS: ViewKind[] = ['DAY', 'WORK_WEEK', 'WEEK'];

const VIEWS: ViewKind[] = ['DAY', 'WORK_WEEK', 'WEEK', 'MONTH', 'YEAR', 'SCHEDULE'];

/** What each view moves by when somebody presses the arrows. */
const STEP: Record<ViewKind, { days?: number; months?: number; years?: number }> = {
  DAY: { days: 1 },
  WORK_WEEK: { days: 7 },
  WEEK: { days: 7 },
  MONTH: { months: 1 },
  YEAR: { years: 1 },
  SCHEDULE: { days: 30 },
};

export function CalendarPage(): React.JSX.Element {
  const client = useQueryClient();

  const [view, setView] = useState<ViewKind>('WEEK');
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [tab, setTab] = useState<'calendar' | 'booking'>('calendar');
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<EventDraft | null>(null);
  const [showWork, setShowWork] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calendars = useQuery({ queryKey: ['calendars'], queryFn: listCalendars });

  const shown = useMemo(
    () => (calendars.data?.calendars ?? []).filter((calendar) => !calendar.hidden),
    [calendars.data?.calendars],
  );

  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  const window = useQuery({
    queryKey: ['calendar-window', range.from.toISOString(), range.to.toISOString()],
    queryFn: () => listWindow({ from: range.from.toISOString(), to: range.to.toISOString() }),
  });

  const agenda = useQuery({
    queryKey: ['agenda', range.from.toISOString(), range.to.toISOString()],
    queryFn: () => getAgenda({ from: range.from.toISOString(), to: range.to.toISOString() }),
    enabled: showWork,
  });

  const bookings = useQuery({ queryKey: ['bookings'], queryFn: listBookings });
  const links = useQuery({ queryKey: ['booking-links'], queryFn: listBookingLinks });

  /** Only what the visible calendars hold. Hiding one takes it off the screen, not out of the data. */
  const items = useMemo(() => {
    const visible = new Set(shown.map((calendar) => calendar.id));

    return (window.data?.items ?? []).filter(
      (item) => item.calendarId === null || visible.has(item.calendarId),
    );
  }, [window.data?.items, shown]);

  const colorOf = useCallback(
    (item: WindowItem): string => {
      if (!isBusy(item) && item.color) return item.color;

      const calendar = shown.find((candidate) => candidate.id === item.calendarId);

      return calendar?.color ?? '#2d5bff';
    },
    [shown],
  );

  /** Moving or resizing a block. Optimism is deliberately absent: the server decides the window. */
  const move = useMutation({
    mutationFn: (input: { item: WindowItem; startsAt: Date; endsAt: Date }) =>
      updateEvent(input.item.id, {
        startsAt: input.startsAt.toISOString(),
        endsAt: input.endsAt.toISOString(),
        // Dragging one occurrence of a series moves that one. Anything wider is a decision, and a
        // decision belongs in the dialog where it can be read before it is made.
        ...(!isBusy(input.item) && input.item.seriesId && input.item.occurrenceStart
          ? { scope: 'THIS' as const, occurrenceStart: input.item.occurrenceStart }
          : {}),
      }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['calendar-window'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const hide = useMutation({
    mutationFn: (input: { id: string; hidden: boolean }) =>
      setCalendarPreference(input.id, { hidden: input.hidden }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['calendars'] }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  // The shortcuts every calendar has. Skipped while somebody is typing, so "d" in a title is a "d".
  useEffect(() => {
    const onKey = (keyEvent: KeyboardEvent): void => {
      const target = keyEvent.target as HTMLElement | null;

      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (keyEvent.metaKey || keyEvent.ctrlKey || keyEvent.altKey) return;

      const shortcut: Record<string, ViewKind> = {
        d: 'DAY',
        x: 'WORK_WEEK',
        s: 'WEEK',
        m: 'MONTH',
        y: 'YEAR',
        a: 'SCHEDULE',
      };

      if (shortcut[keyEvent.key]) {
        setView(shortcut[keyEvent.key]!);
        return;
      }

      if (keyEvent.key === 't') setAnchor(startOfDay(new Date()));
      if (keyEvent.key === 'j' || keyEvent.key === 'ArrowRight') setAnchor(step(view, anchor, 1));
      if (keyEvent.key === 'k' || keyEvent.key === 'ArrowLeft') setAnchor(step(view, anchor, -1));
    };

    globalThis.addEventListener('keydown', onKey);

    return () => globalThis.removeEventListener('keydown', onKey);
  }, [view, anchor]);

  const bookingColumns: Column<BookingRow>[] = [
    {
      key: 'invitee',
      header: t('calendar.invitee'),
      render: (booking) => (
        <span className="cell-stack">
          <span className="strong">{booking.invitee_name}</span>
          <span className="muted small">{booking.invitee_email}</span>
        </span>
      ),
    },
    {
      key: 'when',
      header: t('calendar.when'),
      render: (booking) => formatDateTime(booking.starts_at),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      align: 'end',
      render: (booking) => (
        <Badge tone={booking.status === 'CANCELED' ? 'neutral' : 'positive'}>
          {t(`calendar.booking.${booking.status}`)}
        </Badge>
      ),
    },
  ];

  const openDraft = (startsAt: Date, endsAt: Date, allDay = false): void => {
    setSelected(null);
    setDraft({
      startsAt,
      endsAt,
      allDay,
      ...(shown.find((calendar) => calendar.access === 'WRITE' || calendar.access === 'MANAGE')
        ? {
            calendarId: shown.find(
              (calendar) => calendar.access === 'WRITE' || calendar.access === 'MANAGE',
            )!.id,
          }
        : {}),
    });
  };

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('nav.calendar')}
        description={t('calendar.description')}
        actions={
          <div className="cell-inline">
            <button
              type="button"
              className="button button--primary button--compact"
              onClick={() => openDraft(nextHour(), new Date(nextHour().getTime() + 3600_000))}
            >
              {t('calendar.create')}
            </button>

            <button
              type="button"
              className="button button--quiet button--compact"
              aria-label={t('calendar.previous')}
              onClick={() => setAnchor(step(view, anchor, -1))}
            >
              ←
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              aria-label={t('calendar.next')}
              onClick={() => setAnchor(step(view, anchor, 1))}
            >
              →
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setAnchor(startOfDay(new Date()))}
            >
              {t('calendar.today')}
            </button>

            <span className="strong">{rangeLabel(view, anchor)}</span>
          </div>
        }
      />

      <nav className="tabs" aria-label={t('nav.calendar')}>
        <button
          type="button"
          className={`tab${tab === 'calendar' ? ' is-active' : ''}`}
          onClick={() => setTab('calendar')}
        >
          {t('calendar.tab.calendar')}
        </button>
        <button
          type="button"
          className={`tab${tab === 'booking' ? ' is-active' : ''}`}
          onClick={() => setTab('booking')}
        >
          {t('calendar.tab.availability')}
        </button>
      </nav>

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {tab === 'booking' ? (
        <>
          <Panel title={t('calendar.availability')}>
            <AvailabilityPanel />
          </Panel>

          <Panel title={t('calendar.sync')}>
            <SyncPanel />
          </Panel>

          <div className="home-grid">
            <Panel title={t('calendar.bookings')}>
              <DataTable
                tableKey="calendar"
                rows={bookings.data?.bookings}
                columns={bookingColumns}
                rowKey={(booking) => booking.id}
                isLoading={bookings.isLoading}
                error={bookings.error}
                onRetry={() => void bookings.refetch()}
                empty={{
                  title: t('calendar.noBookings'),
                  description: t('calendar.noBookingsBody'),
                }}
              />
            </Panel>

            <Panel title={t('calendar.links')}>
              <BookingLinks links={links.data?.links ?? []} loading={links.isLoading} />
            </Panel>
          </div>
        </>
      ) : (
        <div className="calendar-layout">
          <aside className="calendar-side">
            <MiniCalendar
              month={anchor}
              selected={anchor}
              onPick={(day) => {
                setAnchor(day);
                if (!GRID_VIEWS.includes(view) && view !== 'SCHEDULE') setView('DAY');
              }}
            />

            <Panel title={t('calendar.myCalendars')}>
              {calendars.isLoading ? (
                <Loading />
              ) : (
                <ul className="calendar-list">
                  {(calendars.data?.calendars ?? []).map((calendar) => (
                    <li key={calendar.id}>
                      <label className="calendar-list__item">
                        <input
                          type="checkbox"
                          checked={!calendar.hidden}
                          onChange={(changeEvent) =>
                            hide.mutate({ id: calendar.id, hidden: !changeEvent.target.checked })
                          }
                        />
                        <span
                          className="calendar-list__dot"
                          style={{ background: calendar.color }}
                          aria-hidden="true"
                        />
                        <span className="cell-stack">
                          <span>{calendar.name}</span>
                          <span className="muted small">
                            {t(`calendar.kind.${calendar.kind}`)}
                            {calendar.access === 'FREE_BUSY'
                              ? ` · ${t('calendar.freeBusyOnly')}`
                              : ''}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <label className="prefs__switch">
              <input
                type="checkbox"
                checked={showWork}
                onChange={(changeEvent) => setShowWork(changeEvent.target.checked)}
              />
              <span className="prefs__switch-text">
                <span>{t('calendar.showWork')}</span>
              </span>
            </label>
          </aside>

          <div className="calendar-main">
            <nav className="tabs tabs--compact" aria-label={t('calendar.view')}>
              {VIEWS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className={`tab${view === kind ? ' is-active' : ''}`}
                  onClick={() => setView(kind)}
                >
                  {t(`calendar.view.${kind}`)}
                </button>
              ))}
            </nav>

            <Panel>
              {window.error ? (
                <ErrorState error={window.error} onRetry={() => void window.refetch()} />
              ) : window.isLoading ? (
                <Loading />
              ) : GRID_VIEWS.includes(view) ? (
                <TimeGrid
                  days={daysOfRange(view, anchor)}
                  items={items}
                  // The same tasks and sprints the month draws; they were missing from the week.
                  work={showWork ? (agenda.data?.items ?? []).filter((it) => it.kind !== 'EVENT') : []}
                  colorOf={colorOf}
                  onOpen={(item) => {
                    if (isBusy(item)) return;
                    setDraft(null);
                    setSelected(item.id);
                  }}
                  onCreate={({ startsAt, endsAt }) => openDraft(startsAt, endsAt)}
                  onMove={(item, next) => move.mutate({ item, ...next })}
                />
              ) : view === 'MONTH' ? (
                <MonthGrid
                  month={anchor}
                  items={items}
                  work={showWork ? (agenda.data?.items ?? []) : []}
                  colorOf={colorOf}
                  onOpenDay={(day) => {
                    setAnchor(day);
                    setView('DAY');
                  }}
                  onOpen={(item) => {
                    if (isBusy(item)) return;
                    setDraft(null);
                    setSelected(item.id);
                  }}
                  onCreate={(day) =>
                    openDraft(
                      new Date(day.getTime() + 9 * 3600_000),
                      new Date(day.getTime() + 10 * 3600_000),
                    )
                  }
                />
              ) : view === 'YEAR' ? (
                <YearGrid
                  year={anchor.getFullYear()}
                  items={items}
                  onPick={(day) => {
                    setAnchor(day);
                    setView('DAY');
                  }}
                />
              ) : (
                <ScheduleList
                  items={items}
                  colorOf={colorOf}
                  onOpen={(item) => {
                    if (isBusy(item)) return;
                    setDraft(null);
                    setSelected(item.id);
                  }}
                />
              )}
            </Panel>
          </div>
        </div>
      )}

      {(selected || draft) && (
        <EventDialog
          eventId={selected}
          draft={draft}
          calendars={calendars.data?.calendars ?? []}
          onClose={() => {
            setSelected(null);
            setDraft(null);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month
// ---------------------------------------------------------------------------

/**
 * The month: a shape rather than a schedule.
 *
 * Each cell shows the first few things and says how many more, because a month cell that tries to
 * list nine meetings shows none of them legibly. Clicking the number opens that day; clicking empty
 * space in a cell starts an event there at nine, which is what somebody clicking on a square means.
 */
function MonthGrid({
  month,
  items,
  work,
  colorOf,
  onOpenDay,
  onOpen,
  onCreate,
}: {
  month: Date;
  items: readonly WindowItem[];
  work: readonly AgendaItem[];
  colorOf: (item: WindowItem) => string;
  onOpenDay: (day: Date) => void;
  onOpen: (item: WindowItem) => void;
  onCreate: (day: Date) => void;
}): React.JSX.Element {
  const byDay = useMemo(() => groupByDay(items), [items]);
  const workByDay = useMemo(
    () => groupAgendaByDay(work.filter((item) => item.kind !== 'EVENT')),
    [work],
  );

  return (
    <div className="calendar">
      {[0, 1, 2, 3, 4, 5, 6].map((weekday) => (
        <div key={weekday} className="calendar__weekday">
          {t(`calendar.weekday.${weekday}`)}
        </div>
      ))}

      {daysOfMonth(month).map((day) => {
        const onThisDay = byDay.get(dayKey(day)) ?? [];

        return (
          <div
            key={day.toISOString()}
            className={`calendar__cell${
              day.getMonth() === month.getMonth() ? '' : ' calendar__cell--outside'
            }${isSameDay(day, new Date()) ? ' calendar__cell--today' : ''}`}
            onDoubleClick={() => onCreate(day)}
          >
            <button
              type="button"
              className="calendar__number"
              onClick={() => onOpenDay(day)}
              aria-label={day.toDateString()}
            >
              {day.getDate()}
            </button>

            {onThisDay.slice(0, 3).map((item) => (
              <button
                key={`${item.id}-${item.startsAt}`}
                type="button"
                className={`calendar__event${isBusy(item) ? ' calendar__event--busy' : ''}`}
                style={isBusy(item) ? {} : { borderInlineStartColor: colorOf(item) }}
                onClick={() => onOpen(item)}
              >
                {isBusy(item)
                  ? t('calendar.busy')
                  : `${item.allDay ? '' : `${timeOf(item.startsAt)} `}${item.title}`}
              </button>
            ))}

            {(workByDay.get(dayKey(day)) ?? []).slice(0, 2).map((item) => (
              <Link
                key={item.id}
                to={item.href}
                className={`calendar__work calendar__work--${item.kind.toLowerCase()}`}
                title={t(`calendar.kind.${item.kind}`)}
              >
                {item.detail?.key ? `${item.detail.key} · ` : ''}
                {item.kind === 'SPRINT_START'
                  ? t('calendar.sprintStart').replace('{name}', item.title)
                  : item.kind === 'SPRINT_END'
                    ? t('calendar.sprintEnd').replace('{name}', item.title)
                    : item.title}
              </Link>
            ))}

            {onThisDay.length > 3 && (
              <button type="button" className="calendar__more" onClick={() => onOpenDay(day)}>
                {t('calendar.more').replace('{n}', String(onThisDay.length - 3))}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Year
// ---------------------------------------------------------------------------

/**
 * Twelve months at once, for finding a date rather than reading a day.
 *
 * Days with something on them are marked and nothing else: at this size a title is unreadable, and
 * a year view that tries to show one shows twelve months of noise.
 */
function YearGrid({
  year,
  items,
  onPick,
}: {
  year: number;
  items: readonly WindowItem[];
  onPick: (day: Date) => void;
}): React.JSX.Element {
  const busyDays = useMemo(() => new Set([...groupByDay(items).keys()]), [items]);

  return (
    <div className="yeargrid">
      {Array.from({ length: 12 }, (_, month) => (
        <div key={month} className="yeargrid__month">
          <h3 className="small strong">{t(`calendar.month.${month}`)}</h3>

          <div className="yeargrid__days">
            {[0, 1, 2, 3, 4, 5, 6].map((weekday) => (
              <span key={weekday} className="yeargrid__weekday">
                {t(`calendar.weekday.${weekday}`).slice(0, 1)}
              </span>
            ))}

            {daysOfMonth(new Date(year, month, 1)).map((day) => (
              <button
                key={day.toISOString()}
                type="button"
                className={`yeargrid__day${day.getMonth() === month ? '' : ' is-outside'}${
                  busyDays.has(dayKey(day)) ? ' has-events' : ''
                }${isSameDay(day, new Date()) ? ' is-today' : ''}`}
                onClick={() => onPick(startOfDay(day))}
              >
                {day.getDate()}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Schedule
// ---------------------------------------------------------------------------

/** The plain list: what is coming, in order, with nothing between it and the reader. */
function ScheduleList({
  items,
  colorOf,
  onOpen,
}: {
  items: readonly WindowItem[];
  colorOf: (item: WindowItem) => string;
  onOpen: (item: WindowItem) => void;
}): React.JSX.Element {
  if (items.length === 0) {
    return <EmptyState title={t('calendar.nothing')} description={t('calendar.nothingBody')} />;
  }

  const byDay = groupByDay(items);

  return (
    <ol className="schedule">
      {[...byDay.entries()].map(([key, onThisDay]) => (
        <li key={key} className="schedule__day">
          <h3 className="small strong">{longDate(new Date(`${key}T12:00:00`))}</h3>

          <ul className="home-list">
            {onThisDay.map((item) => (
              <li key={`${item.id}-${item.startsAt}`}>
                <button
                  type="button"
                  className="schedule__item"
                  onClick={() => onOpen(item)}
                  disabled={isBusy(item)}
                >
                  <span
                    className="schedule__dot"
                    style={{ background: colorOf(item) }}
                    aria-hidden="true"
                  />
                  <span className="muted small">
                    {item.allDay ? t('calendar.allDay') : timeOf(item.startsAt)}
                  </span>
                  <span>{isBusy(item) ? t('calendar.busy') : item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function BookingLinks({
  links,
  loading,
}: {
  links: { id: string; name: string; slug: string; is_active: boolean }[];
  loading: boolean;
}): React.JSX.Element {
  if (loading) return <Loading />;
  if (links.length === 0) {
    return <EmptyState title={t('calendar.noLinks')} description={t('calendar.noLinksBody')} />;
  }

  return (
    <ul className="home-list">
      {links.map((link) => (
        <li key={link.id}>
          <span className="cell-inline">
            <span className="strong">{link.name}</span>
            <code>/book/{link.slug}</code>
            <Badge tone={link.is_active ? 'positive' : 'neutral'}>
              {link.is_active ? t('calendar.active') : t('calendar.inactive')}
            </Badge>
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOfWeek(date: Date): Date {
  return addDays(startOfDay(date), -date.getDay());
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function step(view: ViewKind, anchor: Date, direction: number): Date {
  const move = STEP[view];

  if (move.months) return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
  if (move.years) return new Date(anchor.getFullYear() + direction, anchor.getMonth(), 1);

  return addDays(anchor, (move.days ?? 1) * direction);
}

/** The days a grid view draws. A working week is Monday to Friday, which is what "x" is for. */
function daysOfRange(view: ViewKind, anchor: Date): Date[] {
  if (view === 'DAY') return [startOfDay(anchor)];

  const sunday = startOfWeek(anchor);

  return view === 'WORK_WEEK'
    ? [1, 2, 3, 4, 5].map((offset) => addDays(sunday, offset))
    : [0, 1, 2, 3, 4, 5, 6].map((offset) => addDays(sunday, offset));
}

/** Six weeks from the Sunday before the 1st: the shape every month grid has. */
function daysOfMonth(month: Date): Date[] {
  const first = startOfWeek(startOfMonth(month));

  return Array.from({ length: 42 }, (_, index) => addDays(first, index));
}

/**
 * The window each view asks the server for.
 *
 * Always a whole number of days, and always a little wider than what is drawn, so a meeting that
 * starts just before the first visible hour is still in the data when the grid clips it.
 */
function rangeFor(view: ViewKind, anchor: Date): { from: Date; to: Date } {
  if (view === 'YEAR') {
    return {
      from: new Date(anchor.getFullYear(), 0, 1),
      to: new Date(anchor.getFullYear() + 1, 0, 1),
    };
  }

  if (view === 'MONTH') {
    const days = daysOfMonth(anchor);

    return { from: days[0]!, to: addDays(days.at(-1)!, 1) };
  }

  if (view === 'SCHEDULE') {
    return { from: startOfDay(anchor), to: addDays(startOfDay(anchor), 30) };
  }

  const days = daysOfRange(view, anchor);

  return { from: days[0]!, to: addDays(days.at(-1)!, 1) };
}

function rangeLabel(view: ViewKind, anchor: Date): string {
  if (view === 'YEAR') return String(anchor.getFullYear());
  if (view === 'DAY') return longDate(anchor);

  if (view === 'MONTH' || view === 'SCHEDULE') {
    return `${t(`calendar.month.${anchor.getMonth()}`)} ${anchor.getFullYear()}`;
  }

  const days = daysOfRange(view, anchor);
  const first = days[0]!;
  const last = days.at(-1)!;

  return `${first.getDate()} – ${last.getDate()} ${t(`calendar.month.${last.getMonth()}`)}`;
}

function longDate(date: Date): string {
  return `${t(`calendar.weekday.${date.getDay()}`)}, ${date.getDate()} ${t(
    `calendar.month.${date.getMonth()}`,
  )}`;
}


function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}

function timeOf(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function nextHour(): Date {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1);

  return date;
}

/**
 * Groups by the day each thing *starts*, in order.
 *
 * A multi-day event appears on its first day in these views; the grid handles spanning separately,
 * because a month cell listing the same meeting five times is a month nobody can read.
 */
function groupByDay(items: readonly WindowItem[]): Map<string, WindowItem[]> {
  const out = new Map<string, WindowItem[]>();

  for (const item of [...items].sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))) {
    const key = dayKey(new Date(item.startsAt));

    out.set(key, [...(out.get(key) ?? []), item]);
  }

  return out;
}

function groupAgendaByDay(items: readonly AgendaItem[]): Map<string, AgendaItem[]> {
  const out = new Map<string, AgendaItem[]>();

  for (const item of items) {
    const key = dayKey(new Date(item.startsAt));

    out.set(key, [...(out.get(key) ?? []), item]);
  }

  return out;
}
